import jsPDF from "jspdf";
import QRCode from "qrcode";

/**
 * Standard NIN Slip PDF Generator
 *
 * This utility generates PDF slips for standard NIN verification results.
 * It uses jsPDF directly to create an A4 PDF with both front and back template images.
 * The front template (standard.jpg) has personal data overlaid on it, and the back template
 * (standard-back.jpg) is positioned below with some spacing between them.
 * Both templates are centered on the A4 page.
 */

interface StandardNINVerificationData {
  nin: string;
  lastName: string;
  firstName: string;
  middleName: string;
  gender: string;
  dateOfBirth: string;
  image?: string;
  issueDate?: string;
}

export const generateStandardNINSlipPDF = async (
  data: StandardNINVerificationData
): Promise<void> => {
  // Create A4 PDF in portrait mode
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // Constants for optimization
  const PAGE_WIDTH = 210; // A4 width in mm
  const PAGE_HEIGHT = 297; // A4 height in mm
  const STANDARD_CARD_WIDTH = 84.912; // mm (3.343 inches)
  const STANDARD_CARD_HEIGHT = 53.594; // mm (2.11 inches)
  const SPACE_BETWEEN = 1; // mm space between templates

  // Pre-calculate positions
  const totalHeight =
    STANDARD_CARD_HEIGHT + SPACE_BETWEEN + STANDARD_CARD_HEIGHT;
  const startY = (PAGE_HEIGHT - totalHeight) / 2;
  const startX = (PAGE_WIDTH - STANDARD_CARD_WIDTH) / 2;
  const templateStartX = startX;
  const templateStartY = startY;

  // Helper functions
  const formatDateOfBirth = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatNIN = (nin: string) => {
    if (nin.length === 11) {
      return `${nin.slice(0, 4)}  ${nin.slice(4, 7)}  ${nin.slice(7, 11)}`;
    }
    return nin;
  };

  // Pre-format data
  const formattedDOB = formatDateOfBirth(data.dateOfBirth);
  const formattedNIN = formatNIN(data.nin);
  const fullName = `${data.firstName} ${data.middleName}`.toUpperCase();
  const qrData = {
    nin: data.nin,
    name: `${data.lastName} ${data.firstName} ${data.middleName}`.toUpperCase(),
    dob: data.dateOfBirth,
  };

  try {
    // Add instructional text above the NIN slip
    addInstructionalText(doc);

    // Load font and templates in parallel
    const [fontLoaded, frontTemplate, backTemplate] = await Promise.allSettled([
      loadOCRBFont(doc),
      loadImage("/standard.jpg"),
      loadImage("/standard-back.jpg"),
    ]);

    if (fontLoaded.status === "rejected") {
      console.warn("Could not load OCRB font, falling back to helvetica");
    }

    // Add front template
    if (frontTemplate.status === "rejected") {
      throw new Error("Failed to load front template");
    }
    doc.addImage(
      frontTemplate.value,
      "JPEG",
      startX,
      startY,
      STANDARD_CARD_WIDTH,
      STANDARD_CARD_HEIGHT
    );

    // Add watermarks
    addWatermarks(doc, data.nin, templateStartX, templateStartY);

    // Add user photo
    addUserPhoto(doc, data.image, templateStartX, templateStartY);

    // Add personal information
    addPersonalInfo(
      doc,
      data,
      formattedDOB,
      formattedNIN,
      fullName,
      templateStartX,
      templateStartY
    );

    // Generate and add QR code
    await addQRCode(
      doc,
      qrData,
      templateStartX,
      templateStartY,
      STANDARD_CARD_WIDTH
    );

    // Add back template
    if (backTemplate.status === "rejected") {
      throw new Error("Failed to load back template");
    }
    doc.addImage(
      backTemplate.value,
      "JPEG",
      startX,
      startY + STANDARD_CARD_HEIGHT + SPACE_BETWEEN,
      STANDARD_CARD_WIDTH,
      STANDARD_CARD_HEIGHT
    );

    // Save PDF
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `Standard_NIN_Slip_${data.nin}_${timestamp}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error("Error generating standard NIN slip PDF:", error);
    throw error;
  }
};

// Optimized helper functions
const addInstructionalText = (doc: jsPDF): void => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);

  // Position the text above the NIN slip
  const textY = 20; // Position above the slip
  const centerX = 105; // Center of A4 page

  // Add the three lines of instructional text
  doc.text("Please find below your Improved NIN Slip", centerX, textY, {
    align: "center",
  });
  doc.text(
    "You may cut it out of the paper, fold and laminate as desired.",
    centerX,
    textY + 6,
    { align: "center" }
  );
  doc.text(
    "For your security & privacy, please DO NOT permit others to make photocopies of this slip.",
    centerX,
    textY + 12,
    { align: "center" }
  );
};

const loadOCRBFont = async (doc: jsPDF): Promise<void> => {
  const fontPath = "/fonts/OCRB Regular.ttf";
  const fontData = await fetch(fontPath).then((res) => res.arrayBuffer());
  const base64Font = btoa(String.fromCharCode(...new Uint8Array(fontData)));
  doc.addFileToVFS("OCRB-Regular.ttf", base64Font);
  doc.addFont("OCRB-Regular.ttf", "OCRB", "normal");
};

const addWatermarks = (
  doc: jsPDF,
  nin: string,
  templateStartX: number,
  templateStartY: number
): void => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(200, 200, 200);

  const watermarkPositions = [
    { x: templateStartX + 4, y: templateStartY + 8, angle: 0 },
    { x: templateStartX + 70, y: templateStartY + 50, angle: 40 },
    { x: templateStartX + 2, y: templateStartY + 40, angle: -30 },
  ];

  watermarkPositions.forEach((pos) => {
    doc.text(nin, pos.x, pos.y, { angle: pos.angle });
  });

  doc.setTextColor(0, 0, 0);
};

const addUserPhoto = (
  doc: jsPDF,
  image: string | undefined,
  templateStartX: number,
  templateStartY: number
): void => {
  const photoX = templateStartX + 2;
  const photoY = templateStartY + 10;
  const photoWidth = 20;
  const photoHeight = 25;

  if (image) {
    try {
      let imageData = image;
      if (
        !imageData.startsWith("data:") &&
        !imageData.startsWith("http") &&
        !imageData.startsWith("/")
      ) {
        imageData = `data:image/jpeg;base64,${image}`;
      }
      doc.addImage(imageData, "JPEG", photoX, photoY, photoWidth, photoHeight);
    } catch (error) {
      console.log("Could not process user image for PDF");
      drawPhotoPlaceholder(doc, photoX, photoY, photoWidth, photoHeight);
    }
  } else {
    drawPhotoPlaceholder(doc, photoX, photoY, photoWidth, photoHeight);
  }
};

const drawPhotoPlaceholder = (
  doc: jsPDF,
  photoX: number,
  photoY: number,
  photoWidth: number,
  photoHeight: number
): void => {
  doc.setFillColor(200, 200, 200);
  doc.rect(photoX, photoY, photoWidth, photoHeight, "F");
  doc.setFillColor(0, 0, 0);
  doc.text("Photo", photoX + photoWidth / 2 - 5, photoY + photoHeight / 2);
};

const addPersonalInfo = (
  doc: jsPDF,
  data: StandardNINVerificationData,
  formattedDOB: string,
  formattedNIN: string,
  fullName: string,
  templateStartX: number,
  templateStartY: number
): void => {
  const textStartX = templateStartX + 10;
  const textStartY = templateStartY + 20;
  const lineHeight = 7;

  doc.setFont("OCRB", "normal");
  doc.setFontSize(9);

  // Surname
  doc.text(data.lastName.toUpperCase(), textStartX + 14, textStartY);

  // Given Names
  doc.text(fullName, textStartX + 14, textStartY + lineHeight);

  // Date of Birth
  doc.text(formattedDOB, textStartX + 14, textStartY + lineHeight * 2);

  // NIN Number
  doc.setFontSize(14);
  doc.text(formattedNIN, textStartX + 13, textStartY - 2 + lineHeight * 4.2);
};

const addQRCode = async (
  doc: jsPDF,
  qrData: any,
  templateStartX: number,
  templateStartY: number,
  templateWidth: number
): Promise<void> => {
  const qrX = templateStartX + templateWidth - 27.5;
  const qrY = templateStartY + 10;
  const qrSize = 25;

  try {
    const qrCodeDataURL = await QRCode.toDataURL(JSON.stringify(qrData), {
      width: 200,
      margin: 0,
      color: { dark: "#000000", light: "#FFFFFF" },
    });
    doc.addImage(qrCodeDataURL, "PNG", qrX, qrY, qrSize, qrSize);
  } catch (error) {
    console.warn("Failed to generate QR code:", error);
    doc.setFillColor(0, 0, 0);
    doc.rect(qrX, qrY, qrSize, qrSize, "F");
    doc.setFillColor(255, 255, 255);
    doc.rect(qrX + 1, qrY + 1, qrSize - 2, qrSize - 2, "F");
  }
};

// Helper function to load image
const loadImage = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Check if it's already a data URL (base64)
    if (src.startsWith("data:")) {
      resolve(src);
      return;
    }

    // Check if it's base64 data from API (not a data URL and not a regular URL)
    if (
      !src.startsWith("data:") &&
      !src.startsWith("http") &&
      !src.startsWith("/")
    ) {
      // It's base64 data from API, add the proper data URL prefix
      const dataUrl = `data:image/jpeg;base64,${src}`;
      resolve(dataUrl);
      return;
    }

    // Otherwise, treat it as a regular URL
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = src;
  });
};
